package dev.lingvo.site.pages.app.projects.slug.strings

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexGrow
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.shared.KeyUsageDto
import dev.lingvo.shared.PluralCategory
import dev.lingvo.shared.ReviewStatus
import dev.lingvo.shared.TranslationDto
import dev.lingvo.shared.TranslationOrigin
import dev.lingvo.shared.TranslationState
import dev.lingvo.shared.localeInfo
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.loadReloadable
import dev.lingvo.site.components.StateBadge
import dev.lingvo.site.ds.IconSize
import dev.lingvo.site.ds.LAsyncContent
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LBadgeVariant
import dev.lingvo.site.ds.LBotIcon
import dev.lingvo.site.ds.LBreadcrumbItem
import dev.lingvo.site.ds.LBreadcrumbs
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.LLanguagesIcon
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LSectionTitle
import dev.lingvo.site.ds.PageHeader
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.enterStaggered
import dev.lingvo.site.ds.iconSize
import dev.lingvo.site.ds.monoFont
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.tf
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * One string, in every language. The URL you paste into Slack.
 *
 * A permalink rather than a filtered list, because "look at this string" is a message people send,
 * and a link into a list with a search box loses the thing it was pointing at the moment anybody
 * types in the box.
 *
 * **Languages with nothing in them are listed too.** Silence about a locale reads as "not needed
 * here", which is the opposite of what a missing translation means — and this page exists to answer
 * "where are we with this string", which is a question about the gaps.
 */
@Page("/app/projects/{slug}/strings/{keyId}")
@Layout(".components.layouts.AppLayout")
@Composable
fun StringPermalinkPage() {
    val ctx = rememberPageContext()
    val slug = ctx.route.params["slug"].orEmpty()
    val keyId = ctx.route.params["keyId"].orEmpty()
    var refreshKey by remember { mutableStateOf(0) }
    // `loadReloadable`, not `loadAsync(refreshKey)`: passing the counter as a key discards the
    // string before the request goes out, so a retry replaced what somebody was reading with
    // skeleton bars. See the KDoc on `loadReloadable`.
    val state = loadReloadable(slug, keyId, reloadToken = refreshKey) { ApiClient.keyDetail(slug, keyId) }.state

    Column(Modifier.fillMaxWidth().gap(Space.XL)) {
        LAsyncContent(
            state,
            rows = 5,
            loadingLabel = t("app.projects.slug.strings.key_id.loading_the_string", "Loading the string"),
            onRetry = { refreshKey++ },
        ) { loaded ->
            val detail = loaded
            // The content slot is a single element, so the column that spaced these cards apart
            // has to be inside it.
            Column(Modifier.fillMaxWidth().gap(Space.XL)) {
                val byLocale = detail.translations.associateBy { it.locale }
                val source = byLocale[detail.sourceLocale]

                // **The name once, in the H1, set as code.** It used to be printed twice — as the
                // headline and again as a mono line directly under it — which reads as two
                // different facts and is the first thing a reader tries to reconcile.
                PageHeader(
                    title = detail.key.name,
                    subtitle = t("app.projects.slug.strings.key_id.this_string_in_every_language", "This string, in every language."),
                    modifier = StringPermalinkHeaderStyle.toModifier(),
                    eyebrow = {
                        LBreadcrumbs(
                            listOf(
                                LBreadcrumbItem(t("app.projects.slug.strings.key_id.keys", "Keys"), "/app/projects/$slug/keys"),
                                LBreadcrumbItem(detail.key.name),
                            ),
                        )
                    },
                    status = {
                        if (detail.key.isPlural) {
                            LBadge(t("app.projects.slug.strings.key_id.plural", "Plural"), variant = LBadgeVariant.ACCENT)
                        }
                    },
                )

                LCard(Modifier.fillMaxWidth()) {
                    Column(Modifier.fillMaxWidth().gap(Space.SM)) {
                        Row(Modifier.fillMaxWidth().gap(Space.SM), verticalAlignment = Alignment.CenterVertically) {
                            Span(Modifier.flexGrow(1).fontWeight(600).toAttrs()) {
                                Text(tf("app.projects.slug.strings.key_id.source_locale", "Source — {locale}", "locale" to detail.sourceLocale))
                            }
                            detail.key.maxLength?.let { LBadge(tf("app.projects.slug.strings.key_id.max_length", "max {length}", "length" to it)) }
                        }
                        Value(source)
                        detail.key.context?.let { context ->
                            // The single most useful thing a translator can be given, so it is on
                            // the page rather than behind a hover.
                            LMuted(tf("app.projects.slug.strings.key_id.context", "Context: {context}", "context" to context))
                        }
                        if (detail.key.tags.isNotEmpty()) LMuted(tf("app.projects.slug.strings.key_id.tags", "Tags: {tags}", "tags" to detail.key.tags.joinToString(", ")))
                        CodeUsages(detail.usages)
                    }
                }

                detail.locales.forEachIndexed { index, locale ->
                    val translation = byLocale[locale]
                    // One card per language, assembling. The comments nested inside a card are
                    // deliberately not staggered as well: motion inside motion reads as a page
                    // still loading rather than as two lists.
                    LCard(Modifier.fillMaxWidth().enterStaggered(index)) {
                        Column(Modifier.fillMaxWidth().gap(Space.SM)) {
                            Row(
                                Modifier.fillMaxWidth().gap(Space.SM),
                                verticalAlignment = Alignment.CenterVertically,
                            ) {
                                Span(
                                    Modifier
                                        .iconSize(IconSize.MD)
                                        .color(ColorMode.current.toSitePalette().textSecondary)
                                        .styleModifier { property("display", "inline-flex") }
                                        .toAttrs { attr("aria-hidden", "true") },
                                ) { LLanguagesIcon() }
                                // The language, not the code. Every other surface in the product
                                // resolves `de` to German; this page printed the code beside a
                                // translation somebody is being asked to judge.
                                Span(Modifier.flexGrow(1).fontWeight(600).toAttrs()) {
                                    Text(localeInfo(locale).englishName)
                                }
                                StateBadges(translation)
                                LButton(
                                    t("app.projects.slug.strings.key_id.open_in_editor", "Open in editor"),
                                    {
                                        ctx.router.tryRoutingTo(
                                            "/app/projects/$slug/translate/$locale" +
                                                "?key=$keyId&search=${encodeURIComponent(detail.key.name)}",
                                        )
                                    },
                                    variant = LButtonVariant.GHOST,
                                    size = LButtonSize.SM,
                                )
                            }
                            Value(translation)
                            translation?.reviewNote?.takeIf { it.isNotBlank() }?.let { note ->
                                LMuted(tf("app.projects.slug.strings.key_id.reviewer_said", "A reviewer said: {note}", "note" to note))
                            }
                            translation?.updatedAt?.let { LMuted(tf("app.projects.slug.strings.key_id.last_changed", "Last changed {date}.", "date" to it.take(10))) }
                        }
                    }
                }

                if (detail.comments.isNotEmpty()) {
                    LCard(Modifier.fillMaxWidth()) {
                        Column(Modifier.fillMaxWidth().gap(Space.SM)) {
                            LSectionTitle(t("app.projects.slug.strings.key_id.questions", "Questions"))
                            detail.comments.forEach { comment ->
                                Column(Modifier.fillMaxWidth().gap(Space.XS)) {
                                    Row(Modifier.gap(Space.SM)) {
                                        // A comment with no locale was asked about the source
                                        // string, so it is about every language rather than one.
                                        LBadge(
                                            comment.locale?.let { localeInfo(it).englishName }
                                                ?: t("app.projects.slug.strings.key_id.all_languages", "All languages"),
                                        )
                                        if (comment.resolved) {
                                            LBadge(t("app.projects.slug.strings.key_id.answered", "Answered"), variant = LBadgeVariant.SUCCESS)
                                        } else {
                                            LBadge(t("app.projects.slug.strings.key_id.open", "Open"), variant = LBadgeVariant.WARNING)
                                        }
                                    }
                                    P(Modifier.margin(0.px).toAttrs()) { Text(comment.body) }
                                }
                            }
                        }
                    }
                }

            }
        }
    }
}

/**
 * The value, or an honest statement that there is none.
 *
 * A plural key holds its text in [TranslationDto.pluralForms] and leaves `value` null, so rendering
 * `value` alone would report every finished plural as empty.
 */
/**
 * Where this string is used in the code, if anybody has run `lingvo scan`.
 *
 * **The closest thing this product has to the in-context editing every competitor sells**, and
 * deliberately not the same feature: theirs shows a screenshot somebody attached, this shows the
 * line the string is called from. A translator reading `checkout/CartScreen.kt:214` beside
 * `Text(strings.confirm)` knows it is a button and not a heading, which is most of what they were
 * going to learn from the picture.
 *
 * **Absent, never empty.** A string with no recorded usage is a string nobody has scanned for, not
 * a string nobody calls, and rendering "no usages" would state the second. So the section does not
 * appear at all until there is something true to say — the same rule the rest of this page follows
 * for a key with no context and no tags.
 */
@Composable
private fun CodeUsages(usages: List<KeyUsageDto>) {
    if (usages.isEmpty()) return
    Column(Modifier.gap(Space.XXS).fillMaxWidth()) {
        LMuted(
            tPlural(
                "app.projects.slug.strings.key_id.used_in_code",
                usages.size,
                "Used in {count} place in your code",
                "Used in {count} places in your code",
            ),
        )
        usages.forEach { usage ->
            Column(Modifier.gap(0.px).fillMaxWidth()) {
                Span(
                    Modifier
                        .monoFont()
                        .fontSize(TypeScale.Small)
                        .color(ColorMode.current.toSitePalette().textSecondary)
                        // A repository path is one unbreakable token — no spaces, and a real one
                        // runs past a hundred characters. As a flex item its `min-width: auto`
                        // resolves to that width, nothing above it clips, and the card widens past
                        // the viewport taking the whole page's horizontal scroll with it. The same
                        // one line `LCodeBlock` and `LConflictCard` already apply to unbounded text.
                        .styleModifier { property("overflow-wrap", "anywhere") }
                        .toAttrs(),
                ) {
                    // `path:line`, in that exact shape, because it is the string every editor and
                    // every terminal already knows how to open — so the colon is a format, not
                    // punctuation somebody should be offered the chance to translate. i18n-ignore
                    Text("${usage.path}:${usage.line}")  // i18n-ignore — `path:line` is the format every editor and terminal already opens
                }
                usage.snippet?.let { snippet ->
                    Span(
                        Modifier
                            .monoFont()
                            .fontSize(TypeScale.Micro)
                            .color(ColorMode.current.toSitePalette().textMuted)
                            .styleModifier {
                                property("white-space", "pre-wrap")
                                property("overflow-wrap", "anywhere")
                            }
                            .toAttrs(),
                    ) {
                        Text(snippet)
                    }
                }
            }
        }
    }
}

@Composable
private fun Value(translation: TranslationDto?) {
    val forms = translation?.pluralForms.orEmpty()
    when {
        translation == null || (translation.value.isNullOrBlank() && forms.isEmpty()) ->
            LMuted(t("app.projects.slug.strings.key_id.nothing_here_yet", "Nothing here yet."))
        forms.isNotEmpty() -> Column(Modifier.fillMaxWidth().gap(Space.XS)) {
            PluralCategory.entries.filter { it in forms.keys }.forEach { category ->
                P(Modifier.margin(0.px).toAttrs()) {
                    Text(tf(
                        "app.projects.slug.strings.key_id.plural_form",
                        "{category}: {form}",
                        "category" to category.name.lowercase(),
                        "form" to forms[category].orEmpty(),
                    ))
                }
            }
        }
        else -> P(Modifier.margin(0.px).toAttrs()) { Text(translation.value.orEmpty()) }
    }
}

/**
 * The three translation states, said the way the rest of the product says them.
 *
 * **`StateBadge`, not three lowercase English words written here.** "approved", "translated" and
 * "empty" were literals on this page while the editor, the keys table and the review lane all drew
 * the same three states through `StateBadge` — four surfaces, two vocabularies, and this one was
 * reachable by no catalogue.
 */
@Composable
private fun StateBadges(translation: TranslationDto?) {
    val state = translation?.state ?: TranslationState.EMPTY
    StateBadge(state)
    if (translation?.origin == TranslationOrigin.MACHINE && state != TranslationState.APPROVED) {
        // Machine output nobody has signed off, said on the page where somebody is deciding
        // whether this string is done.
        LBadge(
            t("app.projects.slug.strings.key_id.machine", "Machine"),
            variant = LBadgeVariant.WARNING,
            leading = { LBotIcon() },
        )
    }
    if (translation?.reviewStatus == ReviewStatus.CHANGES_REQUESTED) {
        LBadge(t("app.projects.slug.strings.key_id.sent_back", "Sent back"), variant = LBadgeVariant.DANGER)
    }
    if (translation?.stale == true) LBadge(t("app.projects.slug.strings.key_id.source_changed", "Source changed"))
}


private external fun encodeURIComponent(value: String): String

/**
 * The one place a page title is set in code.
 *
 * A key name is an identifier — `checkout.actions.pay_now` — and reading it in the proportional
 * headline face means reading a dot-separated path with letter shapes that were never designed to
 * be compared character by character. It is also why the mono line under the H1 existed at all;
 * this puts the treatment where the name is instead of printing the name twice.
 */
val StringPermalinkHeaderStyle = CssStyle {
    cssRule(" h1") { Modifier.monoFont() }
}
